import {
    CreateNotificationTemplateSchema,
    UpdateNotificationTemplateSchema,
} from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class NotificationTemplateService {
    constructor(api) {
        this.api = api;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(() => this.api.getAll(params), { maxRetries: 2 });
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch notification templates');
        }
    }

    async create(data) {
        const parsed = CreateNotificationTemplateSchema.safeParse(data);
        if (!parsed.success) {
            throw new ApiError(parsed.error.issues[0].message, 400, 'VALIDATION_ERROR');
        }
        try {
            const response = await this.api.create(parsed.data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to create notification template');
        }
    }

    async update(id, data) {
        const parsed = UpdateNotificationTemplateSchema.safeParse(data);
        if (!parsed.success) {
            throw new ApiError(parsed.error.issues[0].message, 400, 'VALIDATION_ERROR');
        }
        try {
            const response = await this.api.update(id, parsed.data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update notification template');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete notification template');
        }
    }

    async getVariableContract(params = {}) {
        try {
            const response = await withRetry(() => this.api.getVariableContract(params), { maxRetries: 2 });
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch notification variable contract');
        }
    }

    async preview(payload) {
        try {
            const response = await this.api.preview(payload);
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to preview notification template');
        }
    }

    async testSend(payload) {
        try {
            const response = await this.api.testSend(payload);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to test-send notification template');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
